import React, { useState, useRef } from 'react';

const SafeVideo = ({
  src,
  poster,
  className = '',
  style = {},
  controls = true,
  preload = "metadata",
  autoGeneratePoster = true,
  ...props
}) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [posterGenerated, setPosterGenerated] = useState(false);
  const [generatedPosterUrl, setGeneratedPosterUrl] = useState(null);
  const [fallbackPoster, setFallbackPoster] = useState(null);
  const videoRef = useRef(null);

  const generatePosterFromVideo = () => {
    if (!videoRef.current || posterGenerated) return;

    try {
      const video = videoRef.current;
      if (video.videoWidth === 0 || video.videoHeight === 0) return;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to data URL
      const dataURL = canvas.toDataURL('image/jpeg', 0.8);
      setGeneratedPosterUrl(dataURL);
      setPosterGenerated(true);
    } catch (error) {
      console.warn('Failed to generate poster from video:', error);
      // Use a solid color fallback for mobile
      setFallbackPoster('data:image/svg+xml;base64,' + btoa(`
        <svg width="800" height="450" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#4a5c2d;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#8dbf4b;stop-opacity:1" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#grad)"/>
          <circle cx="400" cy="225" r="60" fill="white" opacity="0.9"/>
          <polygon points="380,205 380,245 420,225" fill="#4a5c2d"/>
        </svg>
      `));
      setPosterGenerated(true);
    }
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
    if (autoGeneratePoster && !poster) {
      // Wait a bit for video to fully load, then seek to 1 second
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = 1;
        }
      }, 100);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (autoGeneratePoster && !poster && !posterGenerated) {
      // Try to generate poster immediately when metadata is loaded
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = 1;
        }
      }, 200);
    }
  };

  const handleVideoSeeked = () => {
    if (autoGeneratePoster && !poster && !posterGenerated) {
      generatePosterFromVideo();
    }
  };

  // Use provided poster, generated one, or fallback
  const finalPoster = poster || generatedPosterUrl || fallbackPoster;

  return (
    <video
      ref={videoRef}
      src={src}
      poster={finalPoster}
      className={className}
      style={{
        ...style,
        transition: 'opacity 0.3s ease',
        opacity: videoLoaded ? 1 : 0.8,
        backgroundColor: '#f8f9fa' // Light background while loading
      }}
      controls={controls}
      preload={preload}
      onLoadedData={handleVideoLoad}
      onLoadedMetadata={handleVideoLoadedMetadata}
      onSeeked={handleVideoSeeked}
      playsInline
      {...props}
    >
      <source src={src} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
};

export default SafeVideo;
